/**
 * rutaTipo · construcción de la URL de un tipo de activo CON su ámbito.
 *
 * POR QUÉ EXISTE:
 *   El slug NO identifica un tipo. La clave natural es el par
 *   (scope, slug) — en BBDD, `UNIQUE (scope, slug)` sobre
 *   inv_asset_types. Dos tipos homónimos ("Auriculares" interno y
 *   "Auriculares" colaboradores) comparten slug y solo se distinguen
 *   por el ámbito, así que el ámbito tiene que viajar en la URL: un
 *   enlace debe poder compartirse, marcarse y recargarse dando
 *   siempre el mismo resultado.
 *
 *   Este módulo es el ÚNICO sitio donde se compone esa ruta. Si algún
 *   día cambia la forma (segmento, query, prefijo), se cambia aquí.
 *
 * FORMA:
 *   /inventario/interno/<slug>        listado
 *   /inventario/interno/<slug>/<id>   listado con el drawer abierto
 *   /inventario/colab/<slug>[/<id>]   ídem en colaboradores
 *
 *   Los segmentos son los valores literales del ENUM `scope` de
 *   inv_asset_types ('interno' | 'colab'), sin capa de traducción: lo
 *   que se ve en la URL es lo que hay en la columna.
 */

/** Segmentos de ámbito válidos, en el mismo orden que el ENUM de BBDD. */
export const AMBITOS = ['interno', 'colab'];

/**
 * Normaliza un ámbito al valor canónico. Cualquier cosa distinta de
 * 'colab' cae a 'interno' — mismo criterio que `normScope` del backend
 * (assetService.js), para que front y back no discrepen nunca.
 */
export function normAmbito(scope) {
  return scope === 'colab' ? 'colab' : 'interno';
}

/** ¿Es este segmento de URL un ámbito válido? */
export function esAmbito(seg) {
  return seg === 'interno' || seg === 'colab';
}

/**
 * Ruta del listado de un tipo (o del detalle, si se pasa `id`).
 *
 * @param {string} scope  'interno' | 'colab' (se normaliza)
 * @param {string} slug   slug del tipo
 * @param {number|string} [id]  id del activo → abre el drawer
 * @returns {string} ruta absoluta
 */
export function rutaTipo(scope, slug, id) {
  const base = `/inventario/${normAmbito(scope)}/${slug}`;
  return id === undefined || id === null || id === '' ? base : `${base}/${id}`;
}

/** Etiqueta visible del ámbito (la misma en toda la UI del módulo). */
export function etiquetaAmbito(scope) {
  return normAmbito(scope) === 'colab' ? 'Colaboradores' : 'GRUPOLAE';
}
